import type { ContactFieldDto, SegmentDefinitionDto } from '@carbonwebmail/contracts'

/**
 * What a rule can ask, what it can ask with, and what the answer looks like.
 *
 * Two screens build rules: the segment builder, and the filter on the contacts
 * board. They are the same rules — a filter saved as a view *is* a segment, and
 * the same translation decides who a campaign reaches — so a second copy of
 * which operators a field allows would be a second answer to the question that
 * matters most here. The one that drifted would be the one somebody saved.
 *
 * Data and pure functions only. The two screens draw them very differently: one
 * is a page with groups and a live count, the other a popover with a row or
 * two, and that difference is presentation rather than meaning.
 */

/**
 * Taken from the contract rather than redeclared.
 *
 * A local `{ field: string }` compiles and then fights every assignment: the
 * contract's fields are a union of literals, and a widened copy of them is a
 * second definition of what a rule is that drifts the first time one changes.
 */
export type Child = SegmentDefinitionDto['children'][number]
export type Rule = Extract<Child, { field: string }>

/**
 * Everything a rule may ask about: what every tenant has, then what this one
 * declared for itself.
 *
 * A removed field is still offered when a saved rule names it, because the
 * rule keeps working — it reads the value on the contact rather than the
 * declaration — and dropping it from the list would make an existing segment
 * impossible to edit without rebuilding it.
 */
export function fieldOptions(
  declared: readonly ContactFieldDto[],
): readonly (readonly [string, string])[] {
  return [...BUILT_IN, ...declared.map((field) => [`custom:${field.key}`, field.label] as const)]
}

/**
 * Ordered as the board reads left to right — Name, Email, then the two state
 * columns — so the filter's list is the columns somebody is looking at rather
 * than a separate menu they have to translate. Company and the days since the
 * last send follow: both are real questions about a contact, and neither is a
 * column on the board today.
 */
const BUILT_IN: readonly (readonly [string, string])[] = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['status', 'Subscription state'],
  ['suppression', 'Suppression reason'],
  ['company', 'Company'],
  ['lastSentDays', 'Days since last send'],
]

/**
 * The values each field accepts, where they are a fixed set.
 *
 * A select rather than a text box for these: "subscribed" is the only spelling
 * the database accepts, and letting someone type it is offering them a way to
 * build a rule that is refused on save for a reason they cannot see.
 */
export const VALUES: Partial<Record<string, readonly (readonly [string, string])[]>> = {
  status: [
    ['subscribed', 'subscribed'],
    ['unsubscribed', 'unsubscribed'],
    ['cleaned', 'cleaned'],
  ],
  suppression: [
    ['unsubscribed', 'unsubscribed'],
    ['hard_bounce', 'hard bounce'],
    ['complaint', 'complaint'],
    ['manual', 'manual'],
  ],
}

/**
 * Which comparisons a field offers.
 *
 * Mirrors the domain's own table, and deliberately offers no more than it
 * accepts: a builder that lets somebody assemble a rule the server refuses on
 * save has told them nothing about why, at the point they can least act on it.
 *
 * `choice` is for the fields whose values are a fixed set — asking whether a
 * status "contains" something is not a stricter question, it is a meaningless
 * one.
 */
export const OPERATORS: Record<Kind, readonly (readonly [Rule['operator'], string])[]> = {
  choice: [
    ['is', 'is'],
    ['is_not', 'is not'],
  ],
  text: [
    ['contains', 'contains'],
    ['not_contains', 'does not contain'],
    ['is', 'is exactly'],
    ['is_not', 'is not'],
    ['is_empty', 'is empty'],
    ['is_not_empty', 'is not empty'],
  ],
  // An address is text without the presence pair: every contact has one, so
  // "is empty" could only ever match nobody. The domain refuses those two for
  // `email`, and offering what is refused tells somebody nothing at the point
  // they can least act on it.
  address: [
    ['contains', 'contains'],
    ['not_contains', 'does not contain'],
    ['is', 'is exactly'],
    ['is_not', 'is not'],
  ],
  number: [
    ['at_least', 'at least'],
    ['at_most', 'at most'],
    ['is', 'is'],
    ['is_empty', 'is empty'],
    ['is_not_empty', 'is not empty'],
  ],
  days: [
    ['at_most', 'within'],
    ['at_least', 'not for'],
  ],
}

export type Kind = 'choice' | 'text' | 'address' | 'number' | 'days'

/** The operators that ask about presence, so there is no value to type. */
export const VALUELESS: readonly Rule['operator'][] = ['is_empty', 'is_not_empty']

/**
 * A rule names a field, and a custom rule also names which one.
 *
 * The select carries one string, so the two travel joined and are split on
 * the way back out. `custom:` cannot collide with a built-in name because a
 * field key may not contain a colon — that is the domain's rule, not a
 * convention here.
 */
export function fieldValue(rule: Rule): string {
  return rule.field === 'custom' ? `custom:${rule.key ?? ''}` : rule.field
}

export function fieldPatch(chosen: string): Partial<Rule> {
  if (!chosen.startsWith('custom:')) return { field: chosen as Rule['field'], key: undefined }
  return { field: 'custom', key: chosen.slice('custom:'.length) }
}

/**
 * Changing the field clears the value, and the comparison with it.
 *
 * "subscribed" is a sensible state and a nonsensical company name, so carrying
 * it across leaves a rule that looks filled in and means nothing — and the
 * operator goes too, because "at least" belongs to days and "is" to the rest.
 * Every field accepts `is` except `lastSentDays`, which is why that one is
 * named here and nothing else is.
 *
 * Switching between two custom fields counts as changing the field even though
 * `field` stays `custom`: the key is what says which one, and a cohort carried
 * over into a renewal date is the same nonsense wearing a different name.
 *
 * **Both screens patch rules through this.** The board had a copy that kept the
 * old operator and value, so switching a rule to "Days since last send" left it
 * comparing with `is` — which the domain refuses, and the board answered by
 * showing nobody while the control on screen read "within".
 */
export function applyPatch(rule: Rule, patch: Partial<Rule>): Rule {
  const changed =
    (patch.field !== undefined && patch.field !== rule.field) ||
    (patch.field === 'custom' && patch.key !== rule.key)

  if (changed && patch.field !== undefined) {
    const days = patch.field === 'lastSentDays'
    return {
      field: patch.field,
      ...(patch.field === 'custom' ? { key: patch.key } : {}),
      operator: days ? 'at_most' : 'is',
      value: '',
    }
  }
  return { ...rule, ...patch }
}

export function kindOf(rule: Rule, declared: readonly ContactFieldDto[]): Kind {
  if (rule.field === 'lastSentDays') return 'days'
  if (rule.field === 'status' || rule.field === 'suppression') return 'choice'
  if (rule.field === 'email') return 'address'
  if (rule.field === 'name' || rule.field === 'company') return 'text'

  const field = declared.find((candidate) => candidate.key === rule.key)
  // A rule naming a field that has since been removed still works — it reads
  // the value on the contact, not the declaration — so it is shown as text
  // rather than hidden or broken.
  if (field === undefined) return 'text'
  return field.type === 'number' || field.type === 'date' ? 'number' : 'text'
}
